import type { ColumnDef, SortingState, PaginationState, SortingFn } from '@tanstack/table-core';
import type {
  ColumnDefinition,
  DatatableRow,
  SortDefinition,
  DatatableResponse,
} from '../../../types/datatable-types';
import { SortOrder, ColumnFieldType } from '../../../types/datatable-types';

/**
 * Creates type-aware sorting function for client-side datatables
 * @param fieldType - Type of field for comparison logic
 * @returns TanStack sorting function
 */
export function createSortingFn<T extends DatatableRow>(fieldType?: ColumnFieldType): SortingFn<T> {
  return (rowA, rowB, columnId) => {
    const valueA = rowA.getValue(columnId);
    const valueB = rowB.getValue(columnId);

    // Handle nullish values
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    // Numeric comparison
    if (fieldType === ColumnFieldType.Number) {
      const numA = parseFloat(String(valueA));
      const numB = parseFloat(String(valueB));

      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;

      return numA - numB;
    }

    // String comparison with Czech locale
    return String(valueA).localeCompare(String(valueB), 'cs-CZ');
  };
}

/**
 * Transforms backend ColumnDefinition[] into TanStack ColumnDef[]
 * @param columns - Backend column definitions
 * @param hasActionsColumn - Whether to append actions column
 * @param actionRenderer - Function to render action buttons
 * @returns TanStack column definitions
 */
export function createTanStackColumns<T extends DatatableRow>(
  columns: ColumnDefinition[],
  hasActionsColumn: boolean,
  actionRenderer: (row: T) => HTMLElement
): ColumnDef<T>[] {
  const tanstackColumns: ColumnDef<T>[] = columns.map((column) => {
    const colDef: ColumnDef<T> = {
      id: column.FieldName,
      accessorKey: column.FieldName,
      header: column.Title,
      enableSorting: column.Sortable,
    };

    // Add custom sorting function if FieldType is specified
    if (column.FieldType !== undefined) {
      colDef.sortingFn = createSortingFn<T>(column.FieldType);
    }

    return colDef;
  });

  // Append actions column if needed
  if (hasActionsColumn) {
    tanstackColumns.push({
      id: 'actions',
      accessorFn: (row) => row, // Return the entire row for actions
      header: '',
      enableSorting: false,
      cell: (props) => actionRenderer(props.row.original),
      size: 150, // Fixed size for actions column
      minSize: 100,
      maxSize: 200,
    });
  }

  return tanstackColumns;
}

/**
 * Converts TanStack SortingState to backend SortDefinition[]
 * @param sortingState - TanStack sorting state
 * @returns Backend sort definitions (only first sort, single-column sorting)
 */
export function createSortingState(sortingState: SortingState): SortDefinition[] {
  if (!sortingState || sortingState.length === 0) {
    return [];
  }

  // Only take first sort (single-column sorting)
  const firstSort = sortingState[0];
  const sortOrder = firstSort.desc ? SortOrder.Descending : SortOrder.Ascending;

  return [
    {
      FieldName: firstSort.id,
      SortOrder: sortOrder,
    },
  ];
}

/**
 * Extracts pagination parameters from TanStack state
 * @param paginationState - TanStack pagination state
 * @returns PageSize and PageNumber for backend request
 */
export function createPaginationParams(paginationState: PaginationState): {
  PageSize: number;
  PageNumber: number;
} {
  return {
    PageSize: paginationState.pageSize,
    PageNumber: paginationState.pageIndex,
  };
}

/**
 * Parses backend DatatableResponse into usable data
 * @param response - Raw backend response
 * @returns Destructured data, pagination, and queryString
 */
export function parseBackendResponse<T extends DatatableRow>(
  response: DatatableResponse<T>
): {
  data: T[];
  totalCount: number;
  pageNumber: number;
  queryString: string | null;
} {
  return {
    data: response.Data,
    totalCount: response.Pagination?.TotalCount ?? 0,
    pageNumber: response.Pagination?.PageNumber ?? 0,
    queryString: response.QueryString,
  };
}
